import config from 'config'
import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import useInfinitScroll from 'srcReact/helpers/hooks/useInfinitScroll'
import { urlSearchParams } from 'srcReact/helpers/utils'
import { convertToMinutes } from 'srcReact/helpers/datetimeHelper'
import { organizationActions } from 'srcReact/OrganizationPage'
import styles from './styles.module.scss'
import { checkNested } from 'srcReact/helpers/validator'
import { commonActions } from 'srcReact/components'
import Spinner from 'srcReact/components/common2/Spinner'
import Notification from './components/Notification'
import IconButton from 'srcReact/components/common2/IconButton'
import Icon from 'srcReact/components/icons/Icon'
import Modal from 'srcReact/components/common2/Modal'
import Button from 'srcReact/components/common2/Button'
import MobileModal from 'srcReact/components/common2/MobileModal'
import RadioButtonGroup from 'srcReact/components/common2/RadioButtonGroup'
import Popup from 'reactjs-popup'
import { redirectToPage } from 'srcReact/helpers/history'

function NotificationList({ isOpen, closeModal }) {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const [markReadTriggered, setMarkReadTriggered] = useState(false)
  const [notification, setNotification] = useState(null)
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0)
  const [isDeletedContentModalOpen, setIsDeletedContentModalOpen] = useState(false)
  const [isMuteModalOpen, setIsMuteModalOpen] = useState(false)
  const [selectedMinutes, setSelectedMinutes] = useState(0)
  const {
    common: { isMobile, isTablet, isNative },
    organization: {
      current_organization,
      header,
      notification_list: { notifications, notificationStat, loading, setReadSuccess, last_visit, is_temporary_muted }
    }
  } = useSelector((state) => state)

  const getNotifications = () => {
    // nativeapp
    if (isNative) {
      dispatch(organizationActions.getNotificationList(urlSearchParams('organization_id'), 0, pagination))
      return
    }

    if (checkNested(current_organization, 'info', 'id')) {
      dispatch(organizationActions.getNotificationList(current_organization.info.id, 0, pagination))
    }
  }

  const [pagination, setPagination] = useState({ nextOffset: 0, isPagination: false })
  useEffect(() => {
    getNotifications()
    return () => {
      const notificationBtnEle = document.querySelector('#notification_icon_button')
      if (notificationBtnEle) {
        notificationBtnEle.focus()
      }
    }
  }, [])
  useEffect(() => {
    if (header?.notifications?.unread_count > 0) {
      setUnreadNotificationCount(header?.notifications?.unread_count)
    }
  }, [header?.notifications?.unread_count])
  useEffect(() => {
    if (pagination.isPagination) {
      getNotifications()
    }
  }, [pagination])
  useEffect(() => {
    if (markReadTriggered && setReadSuccess) {
      if (notification && notification.link_url) {
        if (isNative && notification.link_url.match(/0\/messenger/)) {
          window.location = config.webUrl + `/0/messenger/native/messenger`
        } else {
          window.location = notification.link_url
        }
      } else {
        dispatch(commonActions.clearNotificationsCount())
        closeModal()
      }
      setNotification(null)
      setMarkReadTriggered(false)
    }
  }, [setReadSuccess])

  const lastNotificationRef = useInfinitScroll(
    loading,
    notificationStat && notificationStat.remaining,
    notificationStat && notificationStat.nextOffset,
    setPagination,
    [loading]
  )

  const markAsRead = (notification) => {
    if (isNative) localStorage.removeItem('lastActiveTab')
    if (notification.has_been_deleted === 1) {
      setIsDeletedContentModalOpen(true)
    } else {
      setIsDeletedContentModalOpen(false)
      if (notification.is_read === 0) {
        setMarkReadTriggered(true)
        setNotification(notification)
        dispatch(organizationActions.markNotificationAsRead(0, notification.id_notification))
      } else if (isNative && notification.link_url.match(/0\/messenger/)) {
        window.location = config.webUrl + `/0/messenger/native/messenger`
      } else {
        window.location = notification.link_url
      }
    }
  }

  const markAllAsRead = () => {
    if (current_organization && checkNested(current_organization, 'info', 'id')) {
      setMarkReadTriggered(true)
      dispatch(organizationActions.markNotificationAsRead(current_organization.info.id, 0))
    }
  }

  let notificationList = notifications
  if (isNative) {
    notificationList = notifications.filter((n) => !n.link_url.match(/0\/organization/))
  }
  let newNotificationList = []
  let last30daysNotificationList = []
  if (!last_visit) {
    newNotificationList = notificationList
  }
  if (last_visit) {
    const notificationLastVisitDate = new Date(last_visit)
    newNotificationList = notificationList?.filter((n) => {
      const notificationDate = new Date(n.date_utc)
      return notificationDate >= notificationLastVisitDate
    })
    last30daysNotificationList = notificationList?.filter((n) => {
      const notificationDate = new Date(n.date_utc)
      return notificationDate < notificationLastVisitDate
    })
  }

  const closeDeletedContentModal = () => {
    setIsDeletedContentModalOpen(false)
  }

  const closeMuteModal = () => {
    setIsMuteModalOpen(false)
  }

  const goToSettings = () => {
    closeModal()
    redirectToPage('/0/settings/notifications')
  }

  const openMuteModal = () => {
    if (is_temporary_muted === 1) {
      setSelectedMinutes(0)
      dispatch(organizationActions.notificationTemporaryUnmute())
    } else {
      setIsMuteModalOpen(true)
    }
  }

  const handleMinutesChange = (e) => {
    const value = e.target.value
    setSelectedMinutes(value)
  }

  const handleValidate = () => {
    let minutes = 0
    if (selectedMinutes == 1) {
      minutes = convertToMinutes(1, 'hour')
    } else if (selectedMinutes == 2) {
      minutes = convertToMinutes(1, '4hours')
    } else if (selectedMinutes == 3) {
      minutes = convertToMinutes(1, 'day')
    }
    if (minutes > 0) {
      dispatch(organizationActions.notificationTemporaryMute(minutes))
    }
    closeMuteModal()
  }

  const minutesOptions = [
    { value: 1, label: t('notificationList.mute.options.hour') },
    { value: 2, label: t('notificationList.mute.options.hours') },
    { value: 3, label: t('notificationList.mute.options.day') }
  ]

  const showNotificationListContent = () => {
    return (
      <div tabIndex={0} className={styles.container}>
        {isDeletedContentModalOpen && (
          <Modal
            isOpen={isDeletedContentModalOpen}
            handleClose={closeDeletedContentModal}
            title={t('notificationList.deletedContent')}>
            <Button
              text={t('form.invite_form.already_member_button')}
              customStyle={styles.btnGotit}
              handleClick={closeDeletedContentModal}
            />
          </Modal>
        )}

        {isMuteModalOpen && (
          <Modal
            closeOnDocumentClick={true}
            isOpen={isMuteModalOpen}
            handleClose={closeMuteModal}
            title={t('notificationList.mute.title')}>
            <RadioButtonGroup
              items={minutesOptions}
              selectedItemValue={selectedMinutes.toString()}
              onChange={handleMinutesChange}
              radioContainerCSS={styles.radioContainerCSS}
            />
            <div className={styles.muteButtons}>
              <Button
                text={t('global.cancel')}
                style='secondary'
                customStyle={styles.btnGotit}
                handleClick={closeMuteModal}
              />
              <Button text={t('global.validate')} customStyle={styles.btnGotit} handleClick={handleValidate} />
            </div>
          </Modal>
        )}

        {notificationList && notificationList.length >= 1 && !isNative && (
          <React.Fragment>
            <div className={styles.clearAndClose}>
              <div className={styles.actions}>
                {isMobile && (
                  <IconButton
                    iconName='close24'
                    iconSize='large24'
                    className={styles.closeButton}
                    onClick={closeModal}
                  />
                )}
                <IconButton
                  iconName='setting'
                  iconSize='large24'
                  className={styles.settingButton}
                  onClick={goToSettings}
                />
                <IconButton
                  iconName='mute'
                  iconSize='large24'
                  className={is_temporary_muted === 1 ? styles.muteButton : styles.unmuteButton}
                  onClick={openMuteModal}
                />
              </div>
              <button type='button' className={styles.clearAll} onClick={markAllAsRead}>
                <Icon name='mail-opened' size='large24' />
                {t('notificationList.clearAll')}
              </button>
            </div>
          </React.Fragment>
        )}

        {notificationList && notificationList.length >= 1 ? (
          <div
            className={`${!isNative && styles.notificationsContainer} ${
              isNative && styles.nativeNotificationContainer
            }`}>
            {newNotificationList?.length >= 1 && (
              <React.Fragment>
                <h3 className={styles.notificationTitle}>{t('global.new')}</h3>
                <div className={styles.notificationModal}>
                  {newNotificationList.map((notification, index) => {
                    const isLastNotification = newNotificationList.length === index + 1
                    return (
                      <Notification
                        notification={notification}
                        markAsRead={markAsRead}
                        isLastNotification={isLastNotification}
                        lastNotificationRef={lastNotificationRef}
                      />
                    )
                  })}
                </div>
              </React.Fragment>
            )}
            {last30daysNotificationList?.length >= 1 && (
              <React.Fragment>
                <h3 className={styles.notificationTitle}>{t('notificationList.last30Days')}</h3>
                <div className={styles.notificationModal}>
                  {last30daysNotificationList.map((notification, index) => {
                    const isLastNotification = last30daysNotificationList.length === index + 1
                    return (
                      <Notification
                        notification={notification}
                        markAsRead={markAsRead}
                        isLastNotification={isLastNotification}
                        lastNotificationRef={lastNotificationRef}
                      />
                    )
                  })}
                </div>
              </React.Fragment>
            )}
          </div>
        ) : !loading ? (
          <div className={`${isMobile ? styles.zeroNotificationMobileModal : styles.zeroNotificationModal}`}>
            {t('notificationList.noNotificationMessage')}
          </div>
        ) : (
          <Spinner />
        )}
      </div>
    )
  }

  return isNative ? (
    showNotificationListContent() // Notification content is rendered on the native app
  ) : isMobile && !isNative ? (
    <MobileModal
      className='notificationMobilePopup'
      customContentStyle={{
        height: '90vh',
        backgroundColor: '#fafaff'
      }}
      closeOnDocumentClick={isDeletedContentModalOpen || isMuteModalOpen ? false : true}
      showBackArrow={notifications.length > 0 ? false : true}
      isOpen={isOpen}
      handleClose={closeModal}>
      {showNotificationListContent()}
    </MobileModal>
  ) : (
    <Popup
      onClose={closeModal}
      open={isOpen}
      position='bottom right'
      className='notificationPopup'
      arrow={false}
      closeOnDocumentClick={isDeletedContentModalOpen || isMuteModalOpen ? false : true}
      overlayStyle={{ background: 'transparent' }}>
      {showNotificationListContent()}
    </Popup>
  )
}

export default NotificationList
